import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiService from '../ApiService';
import { getCachedRoutes } from '../cache/routePreCache';
import '../styles/Homepage.css';
import Header from './Header';
import GoogleMap from './Map';
import PlaceCardList from './PlaceCardList';
import StoryModal from './StoryModal';
import ItineraryModal from './ItineraryModal';
import FilterModal from './FilterModal';
import Navigation from './Navigation';
import RouteInfoBox from './RouteInfoBox';
import RoutePlanModal from './RoutePlanModal';
import SaveItineraryModal from './SaveItineraryModal';
import RouteNavigationSheet from './RouteNavigationSheet';
import useGeolocation from '../hooks/useGeolocation';
import useDeviceOrientation from '../hooks/useDeviceOrientation';
import usePlaceSearch from '../hooks/usePlaceSearch';
import useStoryModal from '../hooks/useStoryModal';
import useRoute from '../hooks/useRoute';
import { trackEvent } from '../analytics';

function Homepage({ user, userProfile }) {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [itineraryModalOpen, setItineraryModalOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [routePlanModalOpen, setRoutePlanModalOpen] = useState(false);
  const [saveItineraryModalOpen, setSaveItineraryModalOpen] = useState(false);

  const userName = userProfile?.name || 'User';

  // --- Hooks ---
  const { userLocation, setUserLocation, locationLoading, getCurrentLocation } = useGeolocation();
  const { heading, requestPermission: requestOrientationPermission } = useDeviceOrientation();

  const location = useLocation();
  const navigate = useNavigate();

  const {
    selectedList, setSelectedList, route, setRoute, routeMode, setRouteMode,
    customRouteOrigin, setCustomRouteOrigin,
    customRouteDest, setCustomRouteDest,
    createRoute, handleSelectIconClick, handleRouteMode,
    getPlacesForDisplay, onSelectItinerary, saveRouteOffline, cacheProgress,
  } = useRoute({ user });

  const buildSuggestedRouteName = () => {
    if (customRouteOrigin?.label && customRouteDest?.label) {
      return `${customRouteOrigin.label} → ${customRouteDest.label}`;
    }
    const places = Array.from(selectedList.values());
    if (places.length >= 2) {
      const first = places[0]?.description?.name;
      const last  = places[places.length - 1]?.description?.name;
      if (first && last) return `${first} → ${last}`;
    }
    if (places.length === 1) {
      return places[0]?.description?.name ?? '';
    }
    return '';
  };

  const {
    places, loading, zoom, mapCenter, setMapCenter, fetchPlaces, invalidateSearch, onCenterChanged, handleSearch,
  } = usePlaceSearch({ user, i18n, routeMode, selectedList });

  const {
    storyModal, setStoryModal, selectedPlace,
    closeStoryModal, handlePlaceCardClick, handleMarkerClick,
  } = useStoryModal({ user, i18n });

  // --- Effects ---
  useEffect(() => {
    getCurrentLocation();
  }, [getCurrentLocation]);

  // Load a cached route passed via navigation state (from OfflineRoutesScreen)
  useEffect(() => {
    const cached = location.state?.cachedRoute;
    if (!cached) return;
    const placesMap = new Map(cached.places.map(p => [p.reference, p]));
    setSelectedList(placesMap);
    setRoute(cached.route);
    // Center map on the first place so the map has a valid position
    if (cached.places.length > 0) {
      const first = cached.places[0];
      setMapCenter({ lat: first.location.latitude, lng: first.location.longitude });
    }
    // Clear state so it doesn't re-apply on back-navigation
    window.history.replaceState({}, document.title);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load a suggested itinerary passed via navigation state (from SuggestedRoutes)
  // Resolves waypoints and creates the route but does NOT enter routeMode —
  // the user sees the selected places on the map and starts the route manually.
  useEffect(() => {
    const itinerary = location.state?.suggestedItinerary;
    if (!itinerary) return;
    window.history.replaceState({}, document.title);

    const loadItinerary = async () => {
      const current_selection = new Map();
      for (const place_ref of itinerary.waypoints) {
        if (places.has(place_ref)) {
          const place = places.get(place_ref);
          current_selection.set(place.reference, place);
        } else {
          const place = await fetchExtraPlace(place_ref);
          if (place) current_selection.set(place.reference, place);
        }
      }
      if (itinerary.location?.coordinates) {
        const [lng, lat] = itinerary.location.coordinates;
        setMapCenter({ lat, lng });
      }
      setSelectedList(current_selection);
      await createRoute(current_selection);
    };

    loadItinerary();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cold offline open: if the restored viewport sits outside every downloaded
  // route's bbox, snap to the first downloaded route so MapLibre fetches tiles
  // that are already in the `map-tiles` Cache Storage bucket. Without this the
  // SW returns no-response for every tile and the map is blank with a wall of
  // console errors. If nothing is downloaded at all, send the user to the
  // Offline Content screen — there is nothing else to do.
  useEffect(() => {
    if (location.state?.cachedRoute || location.state?.suggestedItinerary) return;
    if (navigator.onLine) return;

    let cancelled = false;
    (async () => {
      const routes = await getCachedRoutes().catch(() => []);
      if (cancelled) return;
      const downloaded = routes.filter(r => r.tilesDownloaded && Array.isArray(r.bbox));
      if (downloaded.length === 0) {
        navigate('/offline-content', { replace: true });
        return;
      }
      const inWarmedArea = mapCenter && downloaded.some(r => {
        const [minLng, minLat, maxLng, maxLat] = r.bbox;
        return mapCenter.lat >= minLat && mapCenter.lat <= maxLat
            && mapCenter.lng >= minLng && mapCenter.lng <= maxLng;
      });
      if (inWarmedArea) return;
      const [minLng, minLat, maxLng, maxLat] = downloaded[0].bbox;
      setMapCenter({ lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 });
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GPS watch while in route mode
  useEffect(() => {
    let watchId;
    if (routeMode) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
        },
        (error) => console.log('Error getting location:', error.message),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    }
    return () => { if (watchId) navigator.geolocation.clearWatch(watchId); };
  }, [routeMode, setUserLocation]);

  // Center on user GPS only:
  //  (a) once on the first lock per mount when nothing else has set a center
  //      (fresh session, no route, no sessionStorage-restored viewport), or
  //  (b) when the user explicitly taps the "Use my location" button in Header,
  //      which flips recenterRequestedRef.
  // We must NOT snap on every GPS update — that would clobber the viewport
  // while previewing an offline route, after returning from /offline, or any
  // time the user has panned the map themselves.
  const recenterRequestedRef = useRef(false);
  useEffect(() => {
    if (!userLocation || routeMode || route) return;
    if (!mapCenter || recenterRequestedRef.current) {
      setMapCenter(userLocation);
      recenterRequestedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation]);

  const handleUseMyLocation = useCallback(() => {
    recenterRequestedRef.current = true;
    getCurrentLocation();
  }, [getCurrentLocation]);

  // Re-fetch places when map center/zoom changes
  useEffect(() => {
    fetchPlaces();
  }, [fetchPlaces]);

  // --- Small handlers not worth a hook ---
  const handleLocationIconClick = (e, place) => {
    e.stopPropagation();
    if (place.location.latitude && place.location.longitude) {
      setMapCenter({ lat: place.location.latitude, lng: place.location.longitude });
    }
  };

  const handleRemoveIconClick = async (e, place) => {
    e.stopPropagation();
    trackEvent('place_hidden', { place_name: place.description?.name });
    await ApiService.post(`/place/${place.reference}/hide/`, null, user);
    invalidateSearch();
    fetchPlaces();
  };

  const fetchExtraPlace = useCallback(async (place_ref) => {
    const data = await ApiService.post('/place/search/', {
      references: [place_ref],
      language: i18n.language,
      offset: 0,
      limit: 1,
    }, user);
    return data && data.length > 0 ? data[0] : null;
  }, [user, i18n.language]);

  const placesForDisplay = getPlacesForDisplay(places);

  return (
    <div className="homepage">
      <Header
        user={user}
        userName={userName}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearch={(e) => handleSearch(e, searchQuery)}
        getCurrentLocation={handleUseMyLocation}
        locationLoading={locationLoading}
        onFilterClick={() => setFilterOpen(prev => !prev)}
        mapCenter={mapCenter}
      />
      <div className="main-content">
        {cacheProgress.active && !saveItineraryModalOpen && (
          <div className="cache-progress-toast">
            {t('cache.saving_offline', { done: cacheProgress.done, total: cacheProgress.total })}
          </div>
        )}
        <GoogleMap
          user={user}
          center={mapCenter}
          defaultCenter={mapCenter}
          defaultZoom={14}
          places={placesForDisplay}
          polyline={route?.polyline}
          userLocation={userLocation}
          userHeading={heading}
          onRequestOrientationPermission={requestOrientationPermission}
          onCenterChanged={onCenterChanged}
          onMarkerClick={handleMarkerClick}
          selectedList={selectedList}
          zoom={zoom}
          routeOrigin={customRouteOrigin}
          routeDest={customRouteDest}
        />

        {routeMode && route && (
          <Navigation
            route={route}
            userLocation={userLocation}
            handlePlaceCardClick={handlePlaceCardClick}
            placesOnRoute={placesForDisplay}
          />
        )}

        {routeMode && route ? (
          <RouteNavigationSheet
            route={route}
            places={placesForDisplay}
            user={user}
            onClose={handleRouteMode}
            onPlaceClick={handlePlaceCardClick}
            originLabel={customRouteOrigin?.label ?? t('route_plan_modal.current_location')}
            destLabel={customRouteDest?.label ?? t('route_plan_modal.current_location')}
          />
        ) : (
          route && (
            <RouteInfoBox
              route={route}
              stops={selectedList ? selectedList.size : 0}
              onStartClick={() => setRoutePlanModalOpen(true)}
              onSaveClick={() => setSaveItineraryModalOpen(true)}
            />
          )
        )}

        {!(routeMode && route) && (
          <PlaceCardList
            user={user}
            loading={loading}
            places={placesForDisplay}
            selectedPlace={selectedPlace}
            selectedList={selectedList}
            handlePlaceCardClick={handlePlaceCardClick}
            handleLocationIconClick={handleLocationIconClick}
            handleSelectIconClick={(e, place) => handleSelectIconClick(e, place, routeMode)}
            handleRemoveIconClick={handleRemoveIconClick}
            routeMode={routeMode}
          />
        )}
      </div>

      <StoryModal
        user={user}
        storyModal={storyModal}
        setStoryModal={setStoryModal}
        closeStoryModal={closeStoryModal}
      />
      <ItineraryModal
        isOpen={itineraryModalOpen}
        onClose={() => setItineraryModalOpen(false)}
        user={user}
        userLocation={userLocation}
        onSelectItinerary={(e, itinerary) => onSelectItinerary(e, itinerary, places, fetchExtraPlace)}
      />
      <FilterModal
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={(filters) => {
          setFilterOpen(false);
          console.log('Filters applied:', filters);
        }}
      />
      <RoutePlanModal
        isOpen={routePlanModalOpen}
        onClose={() => setRoutePlanModalOpen(false)}
        onEndpointChange={async ({ origin, destination }) => {
          setCustomRouteOrigin(origin);
          setCustomRouteDest(destination);
          await createRoute(selectedList, origin, destination);
        }}
        onStart={async ({ origin, destination }) => {
          setCustomRouteOrigin(origin);
          setCustomRouteDest(destination);
          setRoutePlanModalOpen(false);
          const routeData = await createRoute(selectedList, origin, destination);
          if (routeData) {
            trackEvent('navigation_started', { place_count: selectedList.size });
            setRouteMode(true);
          }
        }}
        userLocation={userLocation}
        initialOrigin={customRouteOrigin}
        initialDest={customRouteDest}
      />
      <SaveItineraryModal
        isOpen={saveItineraryModalOpen}
        onClose={() => setSaveItineraryModalOpen(false)}
        user={user}
        waypoints={Array.from(selectedList.keys())}
        suggestedName={buildSuggestedRouteName()}
        onSaveOffline={saveRouteOffline}
        cacheProgress={cacheProgress}
      />
    </div>
  );
}

export default Homepage;
